/**
 * API key management + minimal docs, in the dashboard. The secret renders
 * exactly once, in the create dialog's success state.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Copy, KeyRound, Plus } from "lucide-react";
import type { ApiKeyDto } from "@hydra-notes/shared";
import { useTaskStats } from "@/data/taskStats";
import { Button } from "@/ui/Button";
import { toast } from "@/ui/toast";
import s from "./dashboard.module.css";

export function ApiKeysSection() {
  const keys = useTaskStats((st) => st.keys);
  const revokeKey = useTaskStats((st) => st.revokeKey);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const examplePrefix = keys[0]?.prefix ?? "hn_yourkey";

  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>
          <KeyRound size={15} /> API keys
        </h2>
        <CreateKeyDialog />
      </div>

      {keys.length === 0 ? (
        <p className={s.mutedText}>
          No keys yet. Create one to add and complete tasks from scripts, shortcuts and automations.
        </p>
      ) : (
        <table className={s.keyTable}>
          <thead>
            <tr>
              <th>Label</th>
              <th>Key</th>
              <th>Scope</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>{k.label || "Unnamed"}</td>
                <td className={s.keyPrefix}>{k.prefix}...</td>
                <td>{k.scope === "write" ? "Read and write" : "Read only"}</td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}</td>
                <td>
                  {confirmRevoke === k.id ? (
                    <span className={s.confirmGroup}>
                      <Button variant="danger" size="sm" onClick={() => void revokeKey(k.id)}>
                        Revoke
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(null)}>
                        Keep
                      </Button>
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(k.id)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <details className={s.docs}>
        <summary>API reference</summary>
        <p className={s.mutedText}>
          Authenticate with <code>Authorization: Bearer &lt;key&gt;</code>. Tasks are addressed by their global
          number. Full CRUD lives under <code>/api/v1</code>.
        </p>
        <DocExample
          label="Add a task to your default list"
          code={`curl -X POST https://notes.hep.gg/api/v1/tasks \\
  -H "Authorization: Bearer ${examplePrefix}..." \\
  -H "Content-Type: application/json" \\
  -d '{"title": "Water the plants", "dueDate": "2026-09-01"}'`}
        />
        <DocExample
          label="List open tasks"
          code={`curl "https://notes.hep.gg/api/v1/tasks?status=open" \\
  -H "Authorization: Bearer ${examplePrefix}..."`}
        />
        <DocExample
          label="Complete task #123"
          code={`curl -X POST https://notes.hep.gg/api/v1/tasks/123/complete \\
  -H "Authorization: Bearer ${examplePrefix}..."`}
        />
        <p className={s.mutedText}>
          Also: <code>GET /api/v1/tasks/:number</code>, <code>PATCH /api/v1/tasks/:number</code>,{" "}
          <code>DELETE /api/v1/tasks/:number</code>, <code>POST .../uncomplete</code>,{" "}
          <code>GET /api/v1/task-lists</code>. Filters: <code>status</code>, <code>listId</code>,{" "}
          <code>dueBefore</code>, <code>dueAfter</code>, <code>cursor</code>.
        </p>
      </details>
    </section>
  );
}

function DocExample({ label, code }: { label: string; code: string }) {
  return (
    <div className={s.docExample}>
      <div className={s.docLabel}>{label}</div>
      <pre className={s.docCode}>
        <code>{code}</code>
        <button
          type="button"
          className={s.docCopy}
          aria-label="Copy example"
          onClick={() => {
            void navigator.clipboard.writeText(code).then(() => toast.success("Copied"));
          }}
        >
          <Copy size={12} />
        </button>
      </pre>
    </div>
  );
}

function CreateKeyDialog() {
  const createKey = useTaskStats((st) => st.createKey);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<"read" | "write">("write");
  const [created, setCreated] = useState<ApiKeyDto | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setLabel("");
    setScope("write");
    setCreated(null);
    setBusy(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Dialog.Trigger asChild>
        <Button variant="subtle" size="sm">
          <Plus size={13} /> New API key
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={s.dialogOverlay} />
        <Dialog.Content className={s.dialogContent}>
          {!created ? (
            <>
              <Dialog.Title className={s.dialogTitle}>New API key</Dialog.Title>
              <label className={s.dialogLabel}>
                Label
                <input
                  className={s.dialogInput}
                  value={label}
                  placeholder="e.g. Home Assistant"
                  maxLength={100}
                  autoFocus
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>
              <label className={s.dialogLabel}>
                Scope
                <select className={s.dialogInput} value={scope} onChange={(e) => setScope(e.target.value as "read" | "write")}>
                  <option value="write">Read and write</option>
                  <option value="read">Read only</option>
                </select>
              </label>
              <div className={s.dialogActions}>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="sm">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  variant="solid"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void createKey(label, scope).then((key) => {
                      setBusy(false);
                      if (key) setCreated(key);
                      else toast.error("Could not create the key. Try again.");
                    });
                  }}
                >
                  Create key
                </Button>
              </div>
            </>
          ) : (
            <>
              <Dialog.Title className={s.dialogTitle}>Copy your key</Dialog.Title>
              <p className={s.mutedText}>This is the only time the full key is shown. Store it somewhere safe.</p>
              <pre className={s.secretBox}>
                <code>{created.secret}</code>
              </pre>
              <div className={s.dialogActions}>
                <Button
                  variant="solid"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(created.secret ?? "").then(() => toast.success("Key copied"));
                  }}
                >
                  <Copy size={13} /> Copy key
                </Button>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="sm">
                    Done
                  </Button>
                </Dialog.Close>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
